import styled from '@emotion/styled';
import Container from '@ep/components/Container';
import GridFilter from '@ep/components/GridFilter';
import Layout from '@ep/components/Layout';
import SEO from '@ep/components/Seo';
import BannerModule, { BannerModuleProps } from '@ep/modules/BannerModule';
import CTACardsModule from '@ep/modules/CTACardsModule';
import PostFeatureModule, {
  PostFeatureModuleProps,
} from '@ep/modules/PostFeatureModule';
import PostGridModule from '@ep/modules/PostGridModule';
import PromoBannerModule, {
  PromoBannerModuleProps,
} from '@ep/modules/PromoBannerModule/PromoBannerModule';
import theme from '@ep/theme';
import {
  Gcms_Asset,
  Gcms_MasterSeriesLandingPage,
  Gcms_Post,
} from '@ep/types/gatsbyTypes';
import getModuleProps from '@ep/utils/getModuleProps';
import getSeoProps from '@ep/utils/getSeoProps';
import useQueryParam from '@ep/utils/useQueryParams';
import { WindowLocation } from '@reach/router';
import { graphql } from 'gatsby';
import React, { useState } from 'react';

export interface MasterSeriesLandingPageProps {
  location: WindowLocation;
  data: {
    gcms: {
      masterSeriesLandingPage: Gcms_MasterSeriesLandingPage;
      recentPosts: Gcms_Post[];
      masterSeriesCuratedPosts: Gcms_Post[];
      heroBackground: Gcms_Asset;
    };
  };
}

const StyledContainer = styled(Container)`
  max-width: ${theme.breakpoints.desktop + 400}px;
  display: flex;
  padding: 5rem 0 5rem;
`;

const StyledGridDiv = styled.div`
  padding: 3rem 0;
  background-color: ${theme.colors.lightGray};
`;

const GridContainer = styled(Container)``;

const StyledDiv = styled('div')`
  background-color: ${theme.colors.lightGray};
  text-align: center;
  p {
    font-size: 1.5em;
    text-transform: capitalize;
    color: ${theme.colors.duskBlue};
  }
`;

function MasterSeriesLandingPage({
  location,
  data,
}: MasterSeriesLandingPageProps) {
  const page = data.gcms.masterSeriesLandingPage;
  const { masterSeriesCuratedPosts, recentPosts } = data.gcms;
  const [tableData, setTableData] = useState(recentPosts);

  const hasBannerSection = Boolean(page.bannerHeading && page.bannerSubheading);
  const hasPromoBannerModule = Boolean(
    page.promoBannerModule && page.promoBannerModule.promoBannerLabel,
  );
  const hasCTACards = Boolean(page.cards?.length);

  return (
    <Layout pathname={location.pathname}>
      <SEO {...getSeoProps(page)} />
      <StyledDiv>
        <StyledContainer background={page.heroBackground}>
          <PostFeatureModule
            {...(getModuleProps(
              page,
              'masterSeries',
            ) as PostFeatureModuleProps)}
            label="MasterSeries"
            posts={recentPosts}
            masterSeriesCuratedPosts={masterSeriesCuratedPosts}
          />
        </StyledContainer>
      </StyledDiv>
      {hasPromoBannerModule && (
        <PromoBannerModule
          {...(getModuleProps(
            page.promoBannerModule,
            'promoBanner',
          ) as PromoBannerModuleProps)}
        />
      )}
      <StyledGridDiv>
        <div id={'gridTop'} />
        <GridContainer>
          <GridFilter
            defaultTableData={recentPosts}
            tableData={tableData}
            setTableData={setTableData}
            defaultTopic={useQueryParam('topic', '')[0]}
          />
          <PostGridModule posts={tableData} />
        </GridContainer>
      </StyledGridDiv>
      {hasBannerSection && (
        <BannerModule
          {...(getModuleProps(page, 'banner') as BannerModuleProps)}
          backgroundColor={theme.colors.mediumBlue}
          color={theme.colors.white}
        />
      )}
      {hasCTACards && (
        <CTACardsModule
          addTopGap={true}
          cards={page.cards!}
          themeColor={page.ctaCardsTheme}
        />
      )}
    </Layout>
  );
}

export const query = graphql`
  query MasterSeriesLandingPageQuery($id: ID!) {
    gcms {
      masterSeriesLandingPage(where: { id: $id }) {
        title
        description
        structuredData
        slug
        featuredImage {
          url
          altText
        }
        heroBackground {
          url
          altText
          vidyardLink
        }
        bannerHeading
        bannerSubheading {
          html
        }
        promoBannerModule {
          promoBannerColor {
            hex
          }
          promoBannerLabel
          promoBannerLabelRight
          promoBannerLink
          promoBannerLinkRight
        }
        ctaCardsTheme
        cards {
          id
          topBorderColor
          heading
          subheading {
            html
          }
          cardImage {
            url
            altText
            vidyardLink
          }
          cardImageLink
          featuresTitle
          features {
            html
          }
          buttonLabel
          buttonLink
        }
        masterSeriesFeaturedPosts {
          id
          displayDate
          title
          description
          slug
          postType
          postTopic
          postTags
          displayDate
          featuredImage {
            url
            altText
            vidyardLink
          }
        }
        masterSeriesCuratedPosts {
          id
          displayDate
          title
          description
          slug
          postType
          postTopic
          postTags
          displayDate
          featuredImage {
            url
            altText
            vidyardLink
          }
        }
      }
      recentPosts: posts(
        where: { postType: Master_Series }
        orderBy: displayDate_DESC
      ) {
        id
        displayDate
        title
        description
        slug
        postType
        postTopic
        postTags
        displayDate
        featuredImage {
          url
          altText
          vidyardLink
        }
      }
    }
  }
`;

export default MasterSeriesLandingPage;
